import React from "react";
import { useTranslation } from "react-i18next";
import { TransformedOrder } from "../types/orderApiTypes";
import Icon from "../../../../common/components/Icon/Icon";

interface OrderTotalsProps {
  order: TransformedOrder;
}

const OrderTotals: React.FC<OrderTotalsProps> = ({ order }) => {
  const { t } = useTranslation();
  const { totals, coupons, paymentMethodInfo } = order;

  return (
    <div className="order-page__totals">
      <h3 className="order-page__totals-title">
        {t("cartTotals.title", "Order Summary")}
      </h3>

      {/* Applied Coupons Section */}
      {coupons && coupons.length > 0 && (
        <div className="order-page__totals-coupons">
          <ul className="order-page__totals-coupons-list">
            {coupons.map((coupon) => (
              <li key={coupon.code} className="order-page__totals-coupon-item">
                <span className="coupon-code">
                  <Icon iconName="ticket-outlined" /> {coupon.code}
                </span>
                <span className="coupon-discount">
                  -{coupon.totals.displayDiscount}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Totals Breakdown */}
      <div className="order-page__totals-rows">
        {/* Subtotal */}
        <div className="order-page__totals-row">
          <span>{t("cartTotals.subtotal", "Subtotal")}</span>
          <span>{totals.totalItems}</span>
        </div>

        {/* Discount */}
        {totals.hasDiscount && (
          <div className="order-page__totals-row">
            <span>{t("cartTotals.discount", "Discount")}</span>
            <span className="is-discount">-{totals.totalDiscount}</span>
          </div>
        )}

        {/* Shipping */}
        <div className="order-page__totals-row">
          <span>{t("cartTotals.shipping", "Shipping")}</span>
          <span>
            {totals.shippingStatus === "free"
              ? t("cartTotals.freeShipping", "Free Shipping!")
              : totals.totalShipping}
          </span>
        </div>

        {/* Fees */}
        {totals.hasFees && (
          <div className="order-page__totals-row">
            <span>{t("cartTotals.fees", "Fees")}</span>
            <span>{totals.totalFees}</span>
          </div>
        )}

        {/* Tax */}
        {totals.hasTax && (
          <div className="order-page__totals-row">
            <span>{t("cartTotals.tax", "Tax")}</span>
            <span>{totals.totalTax}</span>
          </div>
        )}

        {/* Payment Method (Specific to Order View) */}
        <div className="order-page__totals-row">
          <span>{t("orderPage.summary.paymentMethod", "Payment Method")}</span>
          <span>{paymentMethodInfo.title}</span>
        </div>
      </div>

      {/* Grand Total */}
      <div className="order-page__totals-total">
        <span>{t("cartTotals.total", "Total")}</span>
        <span>{totals.totalPrice}</span>
      </div>
    </div>
  );
};

export default OrderTotals;
